import { supabase } from '../lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const CSRF_STORAGE_KEY = 'sesame_sso_csrf';
const SESAME_TOKEN_STORAGE_KEY = 'sesame_sso_token';
const SESAME_ACTIVE_TOKEN_KEY = 'sesame_active_token';

interface InitLoginResponse {
  redirectUrl: string;
  csrfToken: string;
  sesameToken: string;
}

interface CompleteSsoResponse {
  access_token: string;
  refresh_token: string;
  user: {
    email: string;
    name: string;
    role: string;
    isMegaAdmin: boolean;
  };
}

export async function initSsoLogin(): Promise<string> {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/initlogin`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      returnBaseUrl: window.location.origin,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'SSO initialization failed' }));
    throw new Error(err.error || 'SSO initialization failed');
  }

  const data: InitLoginResponse = await response.json();
  localStorage.setItem(CSRF_STORAGE_KEY, data.csrfToken);
  localStorage.setItem(SESAME_TOKEN_STORAGE_KEY, data.sesameToken);
  return data.redirectUrl;
}

export function getStoredSsoTokens(): { csrfToken: string | null; sesameToken: string | null } {
  return {
    csrfToken: localStorage.getItem(CSRF_STORAGE_KEY),
    sesameToken: localStorage.getItem(SESAME_TOKEN_STORAGE_KEY),
  };
}

export async function completeSsoLogin(
  csrfToken: string,
  sesameToken: string
): Promise<CompleteSsoResponse> {
  const stored = getStoredSsoTokens();
  if (!stored.csrfToken || stored.csrfToken !== csrfToken) {
    throw new Error('CSRF token mismatch');
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/check-and-get-rights`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ csrfToken, sesameToken }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'SSO validation failed' }));
    const detail = err.detail ? ` (${err.detail})` : '';
    if (err.debug) {
      console.error('[SSO debug]', JSON.stringify(err.debug, null, 2));
    }
    throw new Error((err.error || 'SSO validation failed') + detail);
  }

  localStorage.removeItem(CSRF_STORAGE_KEY);
  localStorage.removeItem(SESAME_TOKEN_STORAGE_KEY);

  const data: CompleteSsoResponse & { sesame_token?: string } = await response.json();

  if (data.sesame_token) {
    localStorage.setItem(SESAME_ACTIVE_TOKEN_KEY, data.sesame_token);
  }

  const { error: sessionError } = await supabase.auth.setSession({
    access_token: data.access_token,
    refresh_token: data.refresh_token,
  });

  if (sessionError) {
    throw new Error('Failed to establish session: ' + sessionError.message);
  }

  return data;
}

export function clearSsoCsrf(): void {
  localStorage.removeItem(CSRF_STORAGE_KEY);
  localStorage.removeItem(SESAME_TOKEN_STORAGE_KEY);
}

export async function sesamePasswordLogin(
  identifier: string,
  password: string
): Promise<CompleteSsoResponse> {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/sesame-login`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ login: identifier, password }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Authentication failed' }));
    throw new Error(err.error || 'Authentication failed');
  }

  const data: CompleteSsoResponse & { sesame_token?: string } = await response.json();

  if (data.sesame_token) {
    localStorage.setItem(SESAME_ACTIVE_TOKEN_KEY, data.sesame_token);
  }

  const { error: sessionError } = await supabase.auth.setSession({
    access_token: data.access_token,
    refresh_token: data.refresh_token,
  });

  if (sessionError) {
    throw new Error('Failed to establish session: ' + sessionError.message);
  }

  return data;
}

export async function sesameDisconnect(): Promise<void> {
  try {
    const sesameToken = localStorage.getItem(SESAME_ACTIVE_TOKEN_KEY);
    localStorage.removeItem(SESAME_ACTIVE_TOKEN_KEY);

    if (!sesameToken) return;

    await fetch(`${SUPABASE_URL}/functions/v1/sesame-disconnect`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sesame_token: sesameToken }),
    });
  } catch {
    // Never block logout
  }
}
